import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";
import { scrollToId } from "./SmoothScroll";

const KAABA_IMG =
  "https://images.unsplash.com/photo-1579305796288-c534f6cf17ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.15 + i * 0.1, ease: [0.76, 0, 0.24, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.5 + i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const HadjjUmrah = () => {
  const { t } = useLang();
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.02]);

  return (
    <section
      id="hadjj-umrah"
      data-testid="hadjj-section"
      ref={ref}
      className="relative bg-[#0B1416] text-[#F5F1E8] overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-[#1BB6B9]/12 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] rounded-full bg-[#C19B6C]/10 blur-[140px] pointer-events-none" />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-28 md:py-40 lg:py-52">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left: image with clipped frame + parallax */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              {/* Massive Arabic-inspired accent numeral */}
              <div
                aria-hidden
                className="absolute -top-16 -left-6 font-italiana num-outline-light select-none pointer-events-none z-0"
                style={{ fontSize: "clamp(140px, 20vw, 280px)", lineHeight: 1 }}
              >
                ٠٦
              </div>

              <div className="clip-frame relative aspect-[4/5] md:aspect-[3/4] bg-[#12201F] z-10">
                <motion.img
                  src={KAABA_IMG}
                  alt="Hadjj & Umrah — La Mecque"
                  style={{ y: imgY, scale: imgScale }}
                  className="w-full h-full object-cover will-change-transform"
                  loading="lazy"
                />
                {/* subtle veil */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1416]/60 via-transparent to-transparent pointer-events-none" />
                {/* Corner badge */}
                <div className="absolute top-4 left-4 flag-pill bg-[#F5F1E8]/12 backdrop-blur-md text-[#F5F1E8]">
                  <Sparkles size={11} className="mr-2" />
                  {t.hadjj.badge}
                </div>
                {/* Bottom stat card */}
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                  <div>
                    <div className="overline text-[#6EE3E6] mb-2">
                      {t.hadjj.guide_label}
                    </div>
                    <div className="font-heading text-2xl md:text-3xl leading-tight">
                      {t.hadjj.guide_value}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="overline text-[#6EE3E6] mb-2">
                      {t.hadjj.rate_label}
                    </div>
                    <div className="font-heading text-2xl md:text-3xl leading-tight">
                      {t.hadjj.rate_value}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: editorial text */}
          <div className="lg:col-span-6 order-1 lg:order-2 relative z-10">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              className="overline text-[#6EE3E6]"
            >
              — {t.hadjj.eyebrow}
            </motion.div>

            <h2
              data-testid="hadjj-title"
              className="font-heading-tight text-5xl md:text-6xl lg:text-7xl leading-[0.92] mt-6"
            >
              {t.hadjj.title_lines.map((line, i) => (
                <span key={i} className="mask-line">
                  <motion.span
                    variants={lineReveal}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.3 }}
                    custom={i}
                    className="inline-block"
                  >
                    {i === 1 ? (
                      <span className="font-italic text-[#6EE3E6]">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h2>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              custom={1}
              className="mt-10 text-base md:text-lg font-light leading-relaxed opacity-80 max-w-xl"
            >
              {t.hadjj.body}
            </motion.p>

            {/* Feature list */}
            <motion.ul
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              custom={2}
              className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 max-w-xl"
            >
              {t.hadjj.features.map((feat, i) => (
                <li
                  key={i}
                  data-testid={`hadjj-feature-${i}`}
                  className="flex items-start gap-3 text-sm font-light opacity-85"
                >
                  <span className="text-[#6EE3E6] mt-1.5 shrink-0" style={{ fontSize: 6 }}>
                    ●
                  </span>
                  <span>{feat}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              custom={3}
              className="mt-12 flex flex-wrap gap-3"
            >
              <button
                data-testid="hadjj-cta-book"
                onClick={() => scrollToId("booking")}
                className="tt-btn tt-btn-primary"
              >
                {t.hadjj.cta_primary}
                <ArrowUpRight size={14} />
              </button>
              <a
                data-testid="hadjj-cta-whatsapp"
                href="https://wa.me/34680856937?text=Hola%20Travel%20To%20Time%2C%20quisiera%20información%20sobre%20Hadjj%20%26%20Umrah"
                target="_blank"
                rel="noreferrer"
                className="tt-btn tt-btn-ghost"
              >
                {t.hadjj.cta_secondary}
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
